import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

export type ReportClassification = {
  classification?: string;
  reason_codes?: string[];
  [k: string]: unknown;
};

export const DRAFT_WATERMARK_LINES = [
  "DRAFT",
  "UNBOUND METHODOLOGY",
  "NOT FOR INVESTMENT DECISION",
] as const;

export const DRAFT_WATERMARK_TEXT = DRAFT_WATERMARK_LINES.join(" — ");

const LABELS: Record<string, string> = {
  final: "Final / Bound",
  draft_unbound: "Draft / Unbound",
  blocked: "Blocked",
  legacy: "Legacy report — unchanged output",
  unknown: "Unknown",
};

/**
 * Renders the SERVER-derived report classification. The component never
 * computes a classification and cannot be told to hide the draft state.
 */
export default function ReportClassificationBanner({
  classification,
  watermarkRequired,
}: {
  classification: ReportClassification | null | undefined;
  watermarkRequired: boolean;
}) {
  const key = String(classification?.classification ?? "unknown").toLowerCase();
  const label = LABELS[key] ?? key.replace(/_/g, " ");
  const isFinal = key === "final";

  return (
    <Alert variant={isFinal ? "default" : "destructive"}>
      <AlertDescription>
        <span className="flex flex-wrap items-center gap-2">
          <strong>Report classification:</strong>
          <Badge variant={isFinal ? "default" : "destructive"}>{label}</Badge>
          {watermarkRequired && (
            <span className="font-semibold tracking-wide">{DRAFT_WATERMARK_TEXT}</span>
          )}
        </span>
        {watermarkRequired && (
          <span className="mt-1 block text-xs">
            This classification forces a watermark on the cover, on every page, in the PDF
            metadata and in the report manifest. It cannot be suppressed from the browser.
          </span>
        )}
      </AlertDescription>
    </Alert>
  );
}
